import { useEffect, useState } from "react";
import { api, apiError, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Coins, Cpu, Sigma, ArrowsClockwise, DownloadSimple, Warning } from "@phosphor-icons/react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const eur = (v) => (v ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v) => (v ?? 0).toLocaleString("it-IT");

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <Card className="p-4 sm:p-5 flex items-center gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${accent}`}>
        <Icon size={22} weight="fill" className="text-white" />
      </div>
      <div>
        <div className="text-2xl font-bold tracking-tight">{value}</div>
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      </div>
    </Card>
  );
}

function UsageTable({ title, rows, labelKey, labelName }) {
  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-5 py-3 border-b bg-slate-50/60 text-sm font-semibold">{title}</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`table-${labelKey}`}>
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b">
              <th className="px-5 py-2 font-medium">{labelName}</th>
              <th className="px-3 py-2 font-medium text-right">Chiamate</th>
              <th className="px-3 py-2 font-medium text-right">Token in</th>
              <th className="px-3 py-2 font-medium text-right">Token out</th>
              <th className="px-5 py-2 font-medium text-right">Costo stimato</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-6 text-center text-slate-400">Nessun consumo registrato</td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i} className="border-b last:border-0 hover:bg-slate-50/60">
                <td className="px-5 py-2.5 font-medium text-slate-700">{r[labelKey] || "—"}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{num(r.calls)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{num(r.input_tokens)}</td>
                <td className="px-3 py-2.5 text-right tabular-nums text-slate-500">{num(r.output_tokens)}</td>
                <td className="px-5 py-2.5 text-right tabular-nums font-semibold">{eur(r.cost_eur)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function AiUsage() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [inP, setInP] = useState("");
  const [outP, setOutP] = useState("");
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/usage/summary");
      setData(r.data);
      setInP(String(r.data.pricing.input_per_m));
      setOutP(String(r.data.pricing.output_per_m));
      setBudget(String(r.data.budget?.monthly_budget_eur ?? 0));
      const tr = await api.get("/usage/trend");
      setTrend(tr.data.trend || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const savePricing = async () => {
    setSaving(true);
    try {
      await api.put("/usage/pricing", { input_per_m: parseFloat(inP), output_per_m: parseFloat(outP) });
      await api.put("/usage/budget", { monthly_budget_eur: parseFloat(budget) || 0 });
      toast.success("Tariffe e budget aggiornati");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const resetCounters = async () => {
    if (!window.confirm("Azzerare tutti i contatori di consumo AI? L'operazione non è reversibile.")) return;
    try {
      const r = await api.post("/usage/reset");
      toast.success(`Contatori azzerati (${r.data.deleted} record)`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <div className="text-slate-400">Caricamento…</div>;
  const t = data.totals;

  return (
    <div className="space-y-6" data-testid="ai-usage-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Consumi AI / CBA</h1>
          <p className="text-sm text-slate-500 mt-1">
            Monitoraggio token e costo stimato di tutte le operazioni AI.
            {data.since ? ` Dati dal ${new Date(data.since).toLocaleString("it-IT")}.` : " Nessun dato ancora registrato."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => { window.open(`${API}/usage/report.pdf`, "_blank"); }} data-testid="report-pdf-btn">
            <DownloadSimple size={16} className="mr-1.5" /> Report PDF
          </Button>
          <Button variant="outline" onClick={() => { window.open(`${API}/usage/export`, "_blank"); }} data-testid="export-csv-btn">
            <DownloadSimple size={16} className="mr-1.5" /> Esporta CSV
          </Button>
          <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={resetCounters} data-testid="reset-counters-btn">
            <ArrowsClockwise size={16} className="mr-1.5" /> Azzera
          </Button>
        </div>
      </div>

      {(data.alerts || []).length > 0 && (
        <Card className="p-4 border-l-4 border-l-red-500 bg-red-50/60" data-testid="budget-alerts">
          <div className="flex items-start gap-3">
            <Warning size={22} weight="fill" className="text-red-500 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-semibold text-red-700">
                Budget mensile superato ({data.current_month}) — soglia {eur(data.budget.monthly_budget_eur)}
              </div>
              <ul className="mt-1 text-sm text-red-700/90 space-y-0.5">
                {data.alerts.map((a, i) => (
                  <li key={i} data-testid={`alert-${i}`}>
                    <b>{a.user_name}</b>: {eur(a.cost_eur)} spesi questo mese
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Sigma} label="Chiamate AI" value={num(t.calls)} accent="bg-gradient-to-br from-indigo-500 to-blue-600" />
        <StatCard icon={Cpu} label="Token input" value={num(t.input_tokens)} accent="bg-gradient-to-br from-sky-500 to-cyan-600" />
        <StatCard icon={Cpu} label="Token output" value={num(t.output_tokens)} accent="bg-gradient-to-br from-violet-500 to-purple-600" />
        <StatCard icon={Coins} label="Costo stimato" value={eur(t.cost_eur)} accent="bg-gradient-to-br from-emerald-500 to-teal-600" />
      </div>

      <Card className="p-5" data-testid="trend-chart-card">
        <div className="text-sm font-semibold mb-4">Andamento spesa AI (mensile)</div>
        {trend.length === 0 ? (
          <div className="text-sm text-slate-400 py-10 text-center">Nessun dato ancora disponibile — il grafico si popola con le prossime operazioni AI.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trend} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" tickFormatter={(v) => `€${v}`} width={60} />
              <Tooltip formatter={(v) => [eur(v), "Costo stimato"]} labelStyle={{ fontWeight: 600 }} />
              <Area type="monotone" dataKey="cost_eur" stroke="#059669" strokeWidth={2.5} fill="url(#costGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card className="p-5">
        <div className="text-sm font-semibold mb-1">Tariffe Claude Sonnet 5 (valori placeholder — modificabili)</div>
        <p className="text-xs text-slate-500 mb-4">La stima in € è ricalcolata su tutta la storia quando modifichi le tariffe.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs text-slate-500">€ / 1M token input</label>
            <Input type="number" step="0.01" value={inP} onChange={(e) => setInP(e.target.value)} className="w-40 mt-1" data-testid="price-input" />
          </div>
          <div>
            <label className="text-xs text-slate-500">€ / 1M token output</label>
            <Input type="number" step="0.01" value={outP} onChange={(e) => setOutP(e.target.value)} className="w-40 mt-1" data-testid="price-output" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Budget mensile per consulente (€)</label>
            <Input type="number" step="1" value={budget} onChange={(e) => setBudget(e.target.value)} className="w-56 mt-1" data-testid="budget-input" placeholder="0 = nessun avviso" />
          </div>
          <Button onClick={savePricing} disabled={saving} data-testid="save-pricing-btn">
            {saving ? "Salvataggio…" : "Salva tariffe e budget"}
          </Button>
        </div>
      </Card>

      <UsageTable title="Consumo per operazione" rows={data.by_operation} labelKey="operation" labelName="Operazione" />
      <UsageTable title="Consumo per cliente" rows={data.by_tenant} labelKey="tenant_name" labelName="Cliente" />
      <UsageTable title="Consumo per consulente" rows={data.by_user} labelKey="user_name" labelName="Consulente" />
    </div>
  );
}
